/**
 * AppHead — gemeinsame sticky Kopfzeile (aus den Design-Prototypen).
 *
 * Dieselbe feste AppBar-Optik wie im Wochenplan: sticky, volle Breite,
 * Trennlinie unten, Zurück-Button links (optional), fetter 18px-Titel,
 * optionale Aktions-Slots und der DE/EN-Sprachumschalter rechts. Ab `sm` wird
 * der Inhalt auf eine 600px-Spalte zentriert.
 *
 * Rein präsentational: kein Datenzugriff, keine Geschäftslogik. Der
 * Sprachumschalter wird aus der bestehenden `LanguageSwitcher`-Komponente
 * wiederverwendet (nicht dupliziert).
 */

import { Link as RouterLink } from "react-router-dom";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import ArrowBack from "@mui/icons-material/ArrowBack";
import Box from "@mui/material/Box";
import { useTheme } from "@mui/material/styles";
import type { ReactNode } from "react";
import LanguageSwitcher from "./LanguageSwitcher";

export default function AppHead({
  title,
  backTo,
  backAriaLabel,
  logoSrc,
  logoSrcDark,
  children,
}: {
  title: string;
  /** Ziel des Zurück-Buttons; ohne Wert wird kein Zurück-Button gerendert. */
  backTo?: string;
  backAriaLabel?: string;
  /**
   * Optionaler Marken-Logo-Pfad. Wenn gesetzt, wird im Marken-Bereich das Logo
   * (statt des Text-Titels) gerendert; `title` dient dann als `alt`-Text für
   * Screenreader. Ohne Wert bleibt der bisherige Text-Titel erhalten.
   */
  logoSrc?: string;
  /**
   * Optionale dunkle Logo-Variante. Wird im Dunkelmodus (theme.palette.mode
   * === "dark") statt `logoSrc` verwendet. Ohne Wert gilt `logoSrc` in beiden
   * Modi.
   */
  logoSrcDark?: string;
  /** Optionaler Aktions-Slot links vom Sprachumschalter. */
  children?: ReactNode;
}) {
  const theme = useTheme();
  const resolvedLogoSrc =
    theme.palette.mode === "dark" && logoSrcDark != null
      ? logoSrcDark
      : logoSrc;
  return (
    <AppBar
      position="sticky"
      color="inherit"
      elevation={0}
      sx={{
        top: "env(safe-area-inset-top, 0px)",
        borderBottom: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        backgroundImage: "none",
        px: { sm: "max(0px, calc((100% - 600px)/2))" },
      }}
    >
      <Toolbar disableGutters sx={{ gap: 0.5, px: 1.5 }}>
        {backTo != null && (
          <IconButton
            component={RouterLink}
            to={backTo}
            aria-label={backAriaLabel}
            sx={{ minWidth: 44, minHeight: 44 }}
          >
            <ArrowBack />
          </IconButton>
        )}
        {resolvedLogoSrc != null ? (
          // Marken-Logo statt Text-Titel; `title` bleibt der alt-Text. Der
          // flex-1-Wrapper schiebt die Aktionen nach rechts, das Bild behält
          // seine natürliche Breite bei fester Höhe.
          <Box sx={{ flex: 1, minWidth: 0, pl: backTo != null ? 0 : 1 }}>
            <Box
              component="img"
              src={resolvedLogoSrc}
              alt={title}
              sx={{
                height: { xs: 34, sm: 44 },
                width: "auto",
                display: "block",
              }}
            />
          </Box>
        ) : (
          <Typography
            fontWeight={700}
            fontSize={18}
            noWrap
            sx={{ flex: 1, minWidth: 0, pl: backTo != null ? 0 : 1 }}
          >
            {title}
          </Typography>
        )}
        {children}
        <LanguageSwitcher />
      </Toolbar>
    </AppBar>
  );
}
